import { readable } from "svelte/store";

export interface RouteState {
  name: string;
  args: Record<string, string>;
  data: Record<string, string>;
}

export interface RouteInput {
  name: string;
  args?: Record<string, string>;
  data?: Record<string, string>;
}

const DEFAULT_ROUTE = "overview";

// ponytail: Static pattern map covers current SPA routes; add dynamic wildcard compiler when arbitrary nested routes are needed.
interface RoutePattern {
  name: string;
  pattern: RegExp;
  keys: string[];
  toPath: (args: Record<string, string>) => string;
}

const PATTERNS: RoutePattern[] = [
  {
    name: "server-backups",
    pattern: /^servers\/([^/]+)\/backups$/,
    keys: ["id"],
    toPath: (a) => `/servers/${encodeURIComponent(a.id || "")}/backups`,
  },
  {
    name: "server-ai",
    pattern: /^servers\/([^/]+)\/ai$/,
    keys: ["id"],
    toPath: (a) => `/servers/${encodeURIComponent(a.id || "")}/ai`,
  },
  { name: "overview", pattern: /^(?:overview)?$/, keys: [], toPath: () => "/overview" },
  { name: "account", pattern: /^account$/, keys: [], toPath: () => "/account" },
  { name: "servers", pattern: /^servers$/, keys: [], toPath: () => "/servers" },
  {
    name: "server-create",
    pattern: /^servers\/create$/,
    keys: [],
    toPath: () => "/servers/create",
  },
  {
    name: "server-home",
    pattern: /^servers\/([^/]+)$/,
    keys: ["id"],
    toPath: (a) => `/servers/${encodeURIComponent(a.id || "")}`,
  },
  {
    name: "server-settings",
    pattern: /^servers\/([^/]+)\/settings$/,
    keys: ["id"],
    toPath: (a) => `/servers/${encodeURIComponent(a.id || "")}/settings`,
  },
  {
    name: "server-console",
    pattern: /^servers\/([^/]+)\/console$/,
    keys: ["id"],
    toPath: (a) => `/servers/${encodeURIComponent(a.id || "")}/console`,
  },
  {
    name: "server-files",
    pattern: /^servers\/([^/]+)\/files$/,
    keys: ["id"],
    toPath: (a) => `/servers/${encodeURIComponent(a.id || "")}/files`,
  },
  {
    name: "server-plugins",
    pattern: /^servers\/([^/]+)\/plugins$/,
    keys: ["id"],
    toPath: (a) => `/servers/${encodeURIComponent(a.id || "")}/plugins`,
  },
  {
    name: "admin-overview",
    pattern: /^(?:admin|admin\/overview)$/,
    keys: [],
    toPath: () => "/admin/overview",
  },
  { name: "admin-stats", pattern: /^admin\/stats$/, keys: [], toPath: () => "/admin/stats" },
  {
    name: "admin-payments",
    pattern: /^admin\/payments$/,
    keys: [],
    toPath: () => "/admin/payments",
  },
  { name: "admin-users", pattern: /^admin\/users$/, keys: [], toPath: () => "/admin/users" },
  {
    name: "admin-user",
    pattern: /^admin\/users\/([^/]+)$/,
    keys: ["id"],
    toPath: (a) => `/admin/users/${encodeURIComponent(a.id || "")}`,
  },
  { name: "admin-servers", pattern: /^admin\/servers$/, keys: [], toPath: () => "/admin/servers" },
  {
    name: "admin-server",
    pattern: /^admin\/servers\/([^/]+)$/,
    keys: ["id"],
    toPath: (a) => `/admin/servers/${encodeURIComponent(a.id || "")}`,
  },
  { name: "admin-nodes", pattern: /^admin\/nodes$/, keys: [], toPath: () => "/admin/nodes" },
  {
    name: "admin-node",
    pattern: /^admin\/nodes\/([^/]+)$/,
    keys: ["id"],
    toPath: (a) => `/admin/nodes/${encodeURIComponent(a.id || "")}`,
  },
];

function decodeFragment(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/** Парсит hash: поддерживает новый path-стиль `#/servers/123/plugins` и старый `#route-name=...`. */
export function parseHash(hash: string): RouteState {
  const raw = hash.replace(/^#\/?/, "");
  if (!raw) {
    return { name: DEFAULT_ROUTE, args: {}, data: {} };
  }

  // Обратная совместимость с форматом route-name=...&route-arg-id=...
  const firstParameterKey = raw.split(/[=&]/, 1)[0] ?? "";
  if (/(?:^|&)route-name=/.test(raw) && !/[/?]/.test(firstParameterKey)) {
    const args: Record<string, string> = {};
    const data: Record<string, string> = {};
    let name = DEFAULT_ROUTE;
    for (const part of raw.split("&")) {
      if (!part) {
        continue;
      }
      const eq = part.indexOf("=");
      const key = eq === -1 ? part : part.slice(0, eq);
      const val = eq === -1 ? "" : decodeFragment(part.slice(eq + 1));
      if (key === "route-name") {
        name = val || DEFAULT_ROUTE;
      } else if (key.startsWith("route-arg-")) {
        args[key.slice("route-arg-".length)] = val;
      } else {
        data[key] = val;
      }
    }
    return { name, args, data };
  }

  // Новый path-формат: /path/to/page?key=val
  const qIndex = raw.indexOf("?");
  const path = (qIndex === -1 ? raw : raw.slice(0, qIndex)).replace(/^\/+|\/+$/g, "");
  const query = qIndex === -1 ? "" : raw.slice(qIndex + 1);

  const data: Record<string, string> = {};
  if (query) {
    for (const part of query.split("&")) {
      if (!part) {
        continue;
      }
      const eq = part.indexOf("=");
      const k = decodeFragment(eq === -1 ? part : part.slice(0, eq));
      const v = eq === -1 ? "" : decodeFragment(part.slice(eq + 1));
      data[k] = v;
    }
  }

  for (const def of PATTERNS) {
    const match = path.match(def.pattern);
    if (match) {
      const args: Record<string, string> = {};
      def.keys.forEach((k, idx) => {
        args[k] = decodeFragment(match[idx + 1] || "");
      });
      return { name: def.name, args, data };
    }
  }

  // Fallback для неизвестного плоского маршрута
  return { name: path || DEFAULT_ROUTE, args: {}, data };
}

export function serializeRoute(route: RouteInput): string {
  const args = route.args ?? {};
  const def = PATTERNS.find((p) => p.name === route.name);
  const path = def ? def.toPath(args) : `/${route.name}`;

  const queryParts: string[] = [];
  for (const [key, value] of Object.entries(route.data ?? {})) {
    queryParts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
  }

  return `#${path}${queryParts.length ? `?${queryParts.join("&")}` : ""}`;
}

function currentRoute(): RouteState {
  return parseHash(window.location.hash);
}

export const route = readable<RouteState>(currentRoute(), (set) => {
  if (!window.location.hash) {
    window.history.replaceState(null, "", serializeRoute({ name: DEFAULT_ROUTE }));
  }
  set(currentRoute());

  const onHashChange = () => set(currentRoute());
  window.addEventListener("hashchange", onHashChange);
  return () => window.removeEventListener("hashchange", onHashChange);
});

export function navigate(name: string, args: Record<string, string> = {}): void {
  const current = currentRoute();
  window.location.hash = serializeRoute({ name, args, data: current.data });
}

export function setRouteData(key: string, value: string | null): void {
  const current = currentRoute();
  const data = { ...current.data };
  if (value === null) {
    delete data[key];
  } else {
    data[key] = value;
  }
  window.location.hash = serializeRoute({ name: current.name, args: current.args, data });
}

export function routeHref(name: string, args: Record<string, string> = {}): string {
  return serializeRoute({ name, args });
}
